import React from "react";
import styled from "styled-components";
import { Icon } from "antd";
import { useTranslation } from "react-i18next";
import Colors from "../../../themes/Colors";

const MAX_VISIBLE_PAGES = 4;
const WINDOW_SIZE = 3;
export const ELLIPSIS = "ellipsis";

const range = (start, end) =>
  Array.from({ length: end - start + 1 }, (_, i) => start + i);

export const getPageItems = (items, page, pageSize) =>
  items.slice((page - 1) * pageSize, page * pageSize);

export const getLastPage = (total, pageSize) =>
  Math.max(1, Math.ceil(total / pageSize));

export const getVisiblePages = (current, lastPage) => {
  if (lastPage <= MAX_VISIBLE_PAGES) return range(1, lastPage);

  const start = Math.min(Math.max(1, current - 1), lastPage - WINDOW_SIZE + 1);
  const end = start + WINDOW_SIZE - 1;

  if (end >= lastPage - 1) return range(lastPage - MAX_VISIBLE_PAGES + 1, lastPage);
  if (end === lastPage - 2) return range(start, lastPage);
  return [...range(start, end), ELLIPSIS, lastPage];
};

const PaginationHolder = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  padding: 10px 16px;
  border-top: 1px solid ${Colors.gray};
  background: ${Colors.snow};

  .listPagination__label {
    font-size: 11px;
    font-weight: 600;
    color: ${Colors.coal};
  }

  .listPagination__pages {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .listPagination__item {
    min-width: 32px;
    height: 32px;
    padding: 0 6px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    color: ${Colors.coal};
    background: ${Colors.snow};
    border: 1px solid ${Colors.gray};
    border-radius: 4px;
    cursor: pointer;
    transition: color 0.2s, border-color 0.2s;
  }

  .listPagination__item:hover:not(:disabled),
  .listPagination__item--active {
    color: ${Colors.primary};
    border-color: ${Colors.primary};
  }

  .listPagination__item .anticon {
    font-size: 12px;
  }

  .listPagination__ellipsis {
    font-size: 12px;
    letter-spacing: 1px;
    color: ${Colors.grayMedium};
  }

  &.listPagination--small {
    padding: 10px 12px;

    .listPagination__pages {
      gap: 4px;
    }

    .listPagination__item {
      min-width: 28px;
      height: 28px;
      padding: 0 4px;
      font-size: 13px;
    }
  }

  .listPagination__item:disabled {
    color: ${Colors.grayMedium};
    cursor: not-allowed;
  }
`;

const ListPagination = ({
  total,
  pageSize,
  current,
  onChange,
  className,
  size,
  labelKey = "Showing {{shown}} of {{total}}",
}) => {
  const { t } = useTranslation();

  if (total <= pageSize) return null;

  const lastPage = getLastPage(total, pageSize);
  const shown = Math.min(pageSize, total - (current - 1) * pageSize);

  return (
    <PaginationHolder className={`${size === "small" ? "listPagination--small " : ""}${className || ""}`}>
      <span className="listPagination__label">
        {t(labelKey, { shown, total })}
      </span>
      <div className="listPagination__pages">
        <button
          type="button"
          className="listPagination__item"
          disabled={current === 1}
          onClick={() => onChange(current - 1)}
        >
          <Icon type="left" />
        </button>
        {getVisiblePages(current, lastPage).map((page) => page === ELLIPSIS ? (
          <span key={page} className="listPagination__ellipsis">•••</span>
        ) : (
          <button
            key={page}
            type="button"
            className={`listPagination__item${page === current ? " listPagination__item--active" : ""}`}
            onClick={() => page !== current && onChange(page)}
          >
            {page}
          </button>
        ))}
        <button
          type="button"
          className="listPagination__item"
          disabled={current === lastPage}
          onClick={() => onChange(current + 1)}
        >
          <Icon type="right" />
        </button>
      </div>
    </PaginationHolder>
  );
};

export default ListPagination;
